import * as yup from 'yup';
import { apiEditProjectV2 } from '../../helpers/api';
import { getCategoryFields, getNameFields, getProjectTypeFileds, nameSchema } from './fieldsConfig';
import { useFilterDropdownOptions } from '../../hooks/useFilterDropdownOptions';
import { PROJECT_FILTER_TYPE } from '../../constants/constants';
import { EditSection } from '../EditSection/EditSection';
import { EditPopupUI } from '../EditPopupUI/EditPopupUI';
import { useRoomTypeOptions } from '../../hooks/useRoomTypes';
import ReactTimeAgo from 'react-time-ago';
import { Form } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { errorToast } from '../../helpers/toastHelper';
import { EditPopupButtonsUI } from '../EditPopupUI/EditPopupButtonsUI';

export const EditProjectPopup = ({ data, show, onClose, loading, onUpdateSuccess }) => {
  if (!show || !data?.id) return null;

  return (
    <EditProjectPopupUI
      data={data}
      show={show}
      onClose={onClose}
      loading={loading}
      onUpdateSuccess={onUpdateSuccess}
    />
  );
};

export const EditProjectPopupUI = ({ data, show, onClose, loading, onUpdateSuccess }) => {
  const { roomTypeOptions } = useRoomTypeOptions();
  const { updated_at, member_details } = data || {};

  const projectFilters = useFilterDropdownOptions({
    filterType: PROJECT_FILTER_TYPE,
    fetchData: show,
  });

  const getFilterValue = (filterId) => {
    if (!data?.filteroptions || !projectFilters) return [];

    const ids = data.filteroptions.map((o) => o.id);
    const filter = projectFilters.find((filter) => filter.id === filterId);
    const value = filter.options.filter((category) => ids.includes(category.value));
    return value;
  };

  const onSubmit = async (values) => {
    const updatedData = { filteroptions: [] };

    Object.keys(values).forEach((key) => {
      if (key.startsWith('filter_')) {
        const options = values[key];
        updatedData.filteroptions.push(...options.map((c) => c.value));
      } else {
        updatedData[key] = values[key];
      }
    });

    const response = await apiEditProjectV2({
      id: data.id,
      payload: updatedData,
    });

    if (response) {
      onUpdateSuccess?.(response);
    }
  };

  const username = member_details && (member_details.name || member_details.first_name && `${member_details.first_name} ${member_details.last_name}` || member_details.email);
  const subtitle = updated_at && <ReactTimeAgo date={Date.parse(updated_at)} />

  const { nameFields, projectTypeFields, defaultValues, schema } = useMemo(() => {
    let schemaObj = {
      ...nameSchema,
    };

    const nameFields = getNameFields(data);
    const projectTypeFields = getProjectTypeFileds(data, roomTypeOptions);
    const fields = [...nameFields, ...projectTypeFields];

    projectFilters.map((filter) => {
      const { fieldsConfig, schema } = getCategoryFields(filter, getFilterValue(filter.id));
      fields.push(...fieldsConfig);
      schemaObj = { ...schemaObj, ...schema };
    });

    const defaultValues = fields.reduce((acc, field) => {
      if (!field.name) return acc;

      acc[field.name] = field.value;
      return acc;
    }, {});

    return {
      nameFields,
      projectTypeFields,
      defaultValues,
      schema: yup.object(schemaObj),
    };
  }, [data, roomTypeOptions, projectFilters]);

  const methods = useForm({
    resolver: schema ? yupResolver(schema) : undefined,
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onInvalid = () => {
    errorToast('Please fix errors', { toastId: 'TOAST_FIX_ERRORS' });
  }

  return (
    <EditPopupUI
      show={show}
      onClose={onClose}
      loading={loading}
      title={data?.name}
      username={username}
      subtitle={subtitle}
      thumbnail={data?.thumbnail}>
      {!loading && data && (
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit, onInvalid)} autoComplete="off">
            {/* name */}
            <EditSection title="Project name" fields={nameFields} />

            {/* project type */}
            <EditSection title="Type of project" fields={projectTypeFields} />

            {/* filters */}
            {projectFilters.map((filter) => {
              const { fieldsConfig } = getCategoryFields(filter, getFilterValue(filter.id));
              return (
                <EditSection
                  key={filter.id}
                  title={filter.label_name}
                  fields={fieldsConfig}
                  previewMode="fields"
                />
              );
            })}

            <EditPopupButtonsUI loading={isSubmitting} onCacel={onClose} />
          </Form>
        </FormProvider>
      )}
    </EditPopupUI>
  );
};
